import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
// Direct icon path: the lucide barrel re-exports ~1,500 modules.
import ArrowUpRight from 'lucide-react/dist/esm/icons/arrow-up-right';
import { EYEBROW, MONO, PILL_DARK, PILL_LIGHT, WRAP } from './register';
import { observe } from './primitives';
import { APP_STORE_URL, PLAY_STORE_URL } from './platform';
import './publicSite.css';

const FONT_HREF =
  'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500&display=swap';

export { APP_STORE_URL, PLAY_STORE_URL };
export const BUSINESS_URL = 'https://business.yunlen.com';

const FONT_STYLE = { fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" };

/* The Yunlen Business lockup: the round mark on an emerald tile that tips
   on hover, a bold wordmark, and the product name set in mono beside it. */
function Wordmark({ label = 'Personal' }) {
  return (
    <span className="group inline-flex items-center gap-2">
      <span className="grid h-8 w-8 shrink-0 place-items-center overflow-hidden rounded-lg bg-[#10b981] transition-transform duration-300 group-hover:rotate-[-8deg]">
        <img src="/brand-mark.png" alt="" width={20} height={20} className="h-5 w-5 object-contain" />
      </span>
      <span className="flex items-baseline gap-1.5 leading-none">
        <span className="text-[1.0625rem] font-bold tracking-[-0.04em] text-[#171717]">Yunlen</span>
        <span className={`${MONO} text-[0.625rem] uppercase tracking-[0.12em] text-[#10b981] max-[429px]:hidden`}>{label}</span>
      </span>
    </span>
  );
}

const SEGMENT_ON = 'rounded-full bg-[#171717] px-3.5 py-2 text-[0.8125rem] font-medium leading-none text-[#ffffff]';
const SEGMENT_OFF =
  'rounded-full px-3.5 py-2 text-[0.8125rem] font-medium leading-none text-[#171717] transition-opacity duration-300 hover:opacity-60';

function PublicHeader({ onDownload }) {
  return (
    // Phones: a floating frosted pill inset from the edges, as on Yunlen Business.
    // From md up: the full-width bar, unchanged.
    <header className="sticky top-0 z-40 px-3 pt-3 md:border-b md:border-[#171717]/[0.06] md:bg-[#ffffff]/80 md:px-0 md:pt-0 md:backdrop-blur-xl md:backdrop-saturate-150">
      <div
        className={`relative z-10 mx-auto flex w-full max-w-[83.125rem] items-center justify-between gap-4 rounded-full bg-[#ffffff]/85 py-2 pl-3 pr-2 md:px-8 lg:px-12 shadow-[0_1px_0_rgba(255,255,255,0.6),0_10px_40px_-20px_rgba(0,0,0,0.3)] ring-1 ring-[#171717]/[0.06] backdrop-blur-xl backdrop-saturate-150 md:rounded-none md:bg-transparent md:py-4 md:shadow-none md:ring-0 md:backdrop-blur-none`}
      >
        <div className="flex min-w-0 items-center gap-3 sm:gap-5">
          <Link to="/" className="shrink-0" aria-label="Yunlen home">
            <Wordmark />
          </Link>
          <nav aria-label="Product" className="hidden items-center rounded-full bg-[#f3f3f3] p-1 md:flex">
            <Link to="/" aria-current="page" className={SEGMENT_ON}>
              Personal
            </Link>
            <a href={BUSINESS_URL} className={SEGMENT_OFF}>
              Business
            </a>
          </nav>
        </div>
        {onDownload ? (
          <Link to="/" className={`${PILL_LIGHT} !px-4 !py-3 !text-[0.8125rem] md:!px-5 md:!py-3.5 md:!text-[0.9375rem]`}>
            What Yunlen does
          </Link>
        ) : (
          <Link to="/download" className={`${PILL_DARK} !px-4 !py-3 !text-[0.8125rem] md:!px-5 md:!py-3.5 md:!text-[0.9375rem]`}>
            Download
            <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        )}
      </div>
    </header>
  );
}

const FOOTER_COLUMNS = [
  {
    title: 'App',
    links: [
      { href: '/download', label: 'Download', internal: true },
      { href: PLAY_STORE_URL, label: 'Google Play' },
      { href: APP_STORE_URL, label: 'App Store' },
    ],
  },
  {
    title: 'Company',
    links: [
      { href: 'https://yunlen.com', label: 'yunlen.com' },
      { href: BUSINESS_URL, label: 'Yunlen Business' },
      { href: 'mailto:contact@yunlen.app', label: 'Support' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { href: 'https://yunlen.com/privacy', label: 'Privacy' },
      { href: 'https://yunlen.com/terms', label: 'Terms' },
    ],
  },
];

const FOOTER_LINK = 'text-[0.875rem] tracking-[-0.01em] text-[#171717] transition-opacity duration-300 hover:opacity-60';
const YEAR = new Date().getFullYear();

function PublicFooter() {
  return (
    <footer className="relative border-t border-[#ececec] bg-[#ffffff]">
      <div className={`${WRAP} grid grid-cols-3 gap-x-6 gap-y-10 py-14 lg:grid-cols-[minmax(0,1.6fr)_repeat(3,auto)] lg:gap-20 lg:py-20`}>
        <div className="col-span-3 max-w-sm lg:col-span-1">
          <Wordmark />
          <p className="mt-5 text-[0.9375rem] leading-snug text-[#565656]">
            The personal app for transfers, accounts, cards, and stablecoins. Companies use Yunlen Business.
          </p>
        </div>
        {FOOTER_COLUMNS.map((column) => (
          <div key={column.title} className="flex flex-col gap-4">
            <span className={`${EYEBROW} text-[#96a4b2]`}>{column.title}</span>
            <div className="flex flex-col gap-3">
              {column.links.map((item) =>
                item.internal ? (
                  <Link key={item.href} to={item.href} className={FOOTER_LINK}>
                    {item.label}
                  </Link>
                ) : (
                  <a
                    key={item.href}
                    href={item.href}
                    className={FOOTER_LINK}
                    {...(item.href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {item.label}
                  </a>
                )
              )}
            </div>
          </div>
        ))}
      </div>
      <div className={`${WRAP} flex flex-wrap items-center justify-between gap-3 border-t border-[#ececec] pb-28 pt-6 md:pb-6`}>
        <span className={`${MONO} text-[0.6875rem] uppercase tracking-[0.12em] text-[#96a4b2]`}>© {YEAR} Yunlen</span>
        <span className={`${MONO} text-[0.6875rem] uppercase tracking-[0.12em] text-[#96a4b2]`}>iPhone · Android</span>
      </div>
    </footer>
  );
}

/* ---------- Mobile get-the-app bar ----------
   Slides up once the hero has scrolled away, so the one action a phone
   visitor came for is always a thumb's reach from the bottom edge. Owns its
   own state, so showing it never re-renders the page. */

function StickyGetApp({ cta }) {
  const sentinel = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const el = sentinel.current;
    if (!el) return undefined;
    return observe(el, (entry) => setShown(!entry.isIntersecting), { threshold: 0, rootMargin: '0px', once: false });
  }, []);

  const button = `${PILL_DARK} !px-5 !py-3.5 shrink-0`;

  return (
    <>
      <div ref={sentinel} aria-hidden="true" className="pointer-events-none absolute left-0 top-0 h-[85vh] w-px" />
      <div
        className={`fixed inset-x-0 bottom-0 z-40 border-t border-[#171717]/[0.06] bg-[#ffffff]/90 px-4 pb-[max(0.875rem,env(safe-area-inset-bottom))] pt-3.5 backdrop-blur-xl transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] md:hidden ${shown ? 'translate-y-0' : 'pointer-events-none translate-y-full'}`}
        aria-hidden={shown ? undefined : 'true'}
      >
        <div className="mx-auto flex max-w-[30rem] items-center gap-3">
          <img src="/ic_launcher.png" alt="" width={40} height={40} className="h-10 w-10 shrink-0 rounded-[0.7rem]" />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="text-[0.9375rem] font-medium leading-tight tracking-[-0.01em] text-[#171717]">Yunlen</span>
            <span className={`${MONO} truncate text-[0.625rem] uppercase tracking-[0.1em] text-[#565656]`}>Free · iPhone and Android</span>
          </span>
          {cta.onClick ? (
            <button type="button" onClick={cta.onClick} className={button} tabIndex={shown ? undefined : -1}>
              {cta.label}
            </button>
          ) : cta.to ? (
            <Link to={cta.to} className={button} tabIndex={shown ? undefined : -1}>
              {cta.label}
            </Link>
          ) : (
            <a href={cta.href} className={button} tabIndex={shown ? undefined : -1}>
              {cta.label}
            </a>
          )}
        </div>
      </div>
    </>
  );
}

export default function PublicShell({ children, cta = null }) {
  const { pathname } = useLocation();
  const onDownload = pathname.toLowerCase().startsWith('/download');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return (
    <div
      className="yl-public relative min-h-screen overflow-x-clip bg-[#ffffff] text-[#171717] antialiased selection:bg-[#10b981]/30 selection:text-[#171717]"
      style={FONT_STYLE}
    >
      <Helmet>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
        <link rel="stylesheet" href={FONT_HREF} />
      </Helmet>
      <a
        href="#main"
        className="sr-only z-50 rounded-full bg-[#171717] px-4 py-2 text-[#ffffff] focus:not-sr-only focus:fixed focus:left-4 focus:top-4"
      >
        Skip to content
      </a>
      <PublicHeader onDownload={onDownload} />
      <main id="main" className="relative">
        {children}
      </main>
      <PublicFooter />
      {cta ? <StickyGetApp cta={cta} /> : null}
    </div>
  );
}
